const GREENHOUSE_API_URL = 'https://boards-api.greenhouse.io/v1/boards/chime/jobs?content=true';

/** 5 minutes in seconds */
const REVALIDATE_INTERVAL = 300;

export const SAVED_JOBS_COOKIE = 'SavedJobs';
export const JOB_ID_SEPARATOR = '%';

export enum GreenhouseCustomFieldId {
  'PayRateType' = 16771689002,
  'OpenToRemote' = 16331643002,
  'Team' = 102503774002,
  'FeaturedJob' = 115390903002,
}

export interface GreenhouseJobLocation {
  name: string;
}

export interface GreenhouseJobMetadata {
  id: GreenhouseCustomFieldId;
  name: string;
  value: string | boolean | null;
  value_type: string;
}

export interface GreenhouseDataCompliance {
  type: string;
  requires_consent: boolean;
  requires_processing_consent: boolean;
  requires_retention_consent: boolean;
  retention_period: string | null;
  demographic_data_consent_applies: boolean;
}

export interface GreenhouseOffice {
  id: number;
  name: string;
  location: string;
  child_ids: number[];
  parent_id: number | null;
}

export interface GreenhouseJob {
  id: number;
  title: string;
  absolute_url: string;
  location: GreenhouseJobLocation;
  content: string;
  updated_at: string;
  internal_job_id?: number;
  requisition_id?: string;
  company_name?: string;
  first_published?: string;
  metadata?: GreenhouseJobMetadata[];
  offices?: GreenhouseOffice[];
  data_compliance?: GreenhouseDataCompliance[];
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

const CORE_STRING_FIELDS = ['title', 'absolute_url', 'updated_at', 'content'] as const;

function isGreenhouseJob(entry: unknown): entry is GreenhouseJob {
  if (!isRecord(entry)) return false;

  if (typeof entry.id !== 'number') return false;
  if (CORE_STRING_FIELDS.some(field => typeof entry[field] !== 'string')) return false;
  if (!isRecord(entry.location) || typeof entry.location.name !== 'string') return false;

  return true;
}

function isGreenhouseJobsResponse(data: unknown): data is { jobs: unknown[]; meta: { total: number } } {
  return isRecord(data) && Array.isArray(data.jobs);
}

/**
 * Fetches and validates the live Greenhouse board, sorted newest-first. Throws on any
 * failure — a non-`ok` response, an unexpected payload shape, or a network error — so
 * callers that must distinguish "the board is genuinely empty" from "the fetch failed"
 * can branch on the exception. A successful response with no jobs returns `[]`.
 */
export async function fetchGreenhouseJobsOrThrow(): Promise<GreenhouseJob[]> {
  const response = await fetch(GREENHOUSE_API_URL, {
    next: { revalidate: REVALIDATE_INTERVAL },
  });

  if (!response.ok) {
    throw new Error(`[Greenhouse] Failed to fetch jobs: ${response.status} ${response.statusText}`);
  }

  const data: unknown = await response.json();

  if (!isGreenhouseJobsResponse(data)) {
    throw new Error('[Greenhouse] Unexpected response shape from Greenhouse API');
  }

  const validJobs = data.jobs.filter(isGreenhouseJob);

  if (validJobs.length < data.jobs.length) {
    console.warn(`[Greenhouse] Dropped ${data.jobs.length - validJobs.length} malformed job entries`);
  }

  return validJobs.sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());
}

/**
 * Non-throwing wrapper around {@link fetchGreenhouseJobsOrThrow}: logs and degrades to `[]`
 * on any failure. Use this everywhere an empty list is an acceptable fallback (e.g. rendering
 * the careers UI); use `fetchGreenhouseJobsOrThrow` only where a failure must surface.
 */
export async function fetchGreenhouseJobs(): Promise<GreenhouseJob[]> {
  try {
    return await fetchGreenhouseJobsOrThrow();
  } catch (error) {
    console.error('[Greenhouse] Error fetching jobs:', error instanceof Error ? error.message : error);
    return [];
  }
}
